import { useState, useRef, useCallback, useEffect } from "react";
import { getChatDraft, setChatDraft } from "../../../../chat-dock/chatDrafts";
import {
  Box,
  TextField,
  Button,
  IconButton,
  Paper,
  Typography,
  alpha,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import ImageIcon from "@mui/icons-material/Image";
import MicIcon from "@mui/icons-material/Mic";
import VideocamIcon from "@mui/icons-material/Videocam";
import { useContactCenterTheme } from "../../../theming/ContactCenterThemeContext";
import { AttachedFile } from "../types/attachedfile";
import { MediaPreviewStrip } from "./MediaPreviewStrip";
import { AudioRecorder } from "./AudioRecorder";

const ACCEPTED_IMAGE = ["image/jpeg", "image/png", "image/gif", "image/webp"];
const ACCEPTED_AUDIO = [
  "audio/mpeg",
  "audio/mp4",
  "audio/ogg",
  "audio/wav",
  "audio/aac",
  "audio/webm",
];
const ACCEPTED_VIDEO = [
  "video/mp4",
  "video/webm",
  "video/ogg",
  "video/quicktime",
  "video/3gpp",
];

const MAX_FILES = 10;
const MAX_SIZE_MB = 5;

interface SendPayload {
  to?: string;
  message?: string;
  files: File[];
}

interface Props {
  to?: string;
  isSending: boolean;
  onSend: (payload: SendPayload) => void;
  draftKey?: string | null;
}

function makeId() {
  return Math.random().toString(36).slice(2);
}

export function MessageInput({ to, isSending, onSend, draftKey }: Props) {
  const { theme } = useContactCenterTheme();
  const [messageText, setMessageText] = useState(() => getChatDraft(draftKey));
  const draftKeyRef = useRef(draftKey);

  useEffect(() => {
    if (draftKeyRef.current === draftKey) {
      setChatDraft(draftKey, messageText);
      return;
    }

    draftKeyRef.current = draftKey;
    setMessageText(getChatDraft(draftKey));
  }, [draftKey, messageText]);
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [showRecorder, setShowRecorder] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const textFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const focusMessageInput = useCallback(() => {
    requestAnimationFrame(() => {
      textFieldRef.current?.focus();
    });
  }, []);

  useEffect(() => {
    if (!isSending && !showRecorder) {
      focusMessageInput();
    }
  }, [isSending, showRecorder, focusMessageInput]);

  const addFiles = useCallback(
    (rawFiles: FileList | File[]) => {
      const list = Array.from(rawFiles);
      const valid: AttachedFile[] = [];

      for (const f of list) {
        if (attachedFiles.length + valid.length >= MAX_FILES) break;
        if (f.size > MAX_SIZE_MB * 1024 * 1024) continue;

        const accepted = [...ACCEPTED_IMAGE, ...ACCEPTED_AUDIO, ...ACCEPTED_VIDEO];
        if (!accepted.includes(f.type)) continue;

        valid.push({
          id: makeId(),
          file: f,
          previewUrl:
            f.type.startsWith("image/") || f.type.startsWith("video/")
              ? URL.createObjectURL(f)
              : undefined,
        });
      }

      setAttachedFiles((prev) => [...prev, ...valid]);
      focusMessageInput();
    },
    [attachedFiles.length, focusMessageInput],
  );

  const removeFile = useCallback(
    (id: string) => {
      setAttachedFiles((prev) => {
        const removed = prev.find((f) => f.id === id);
        if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
        return prev.filter((f) => f.id !== id);
      });
      focusMessageInput();
    },
    [focusMessageInput],
  );

  const handleSend = useCallback(() => {
    if (!messageText.trim() && !attachedFiles.length) return;
    if (isSending) return;

    onSend({
      to,
      message: messageText.trim() || undefined,
      files: attachedFiles.map((f) => f.file),
    });

    setMessageText("");
    setAttachedFiles([]);
    focusMessageInput();
  }, [messageText, attachedFiles, isSending, onSend, to, focusMessageInput]);

  const handleAutoSendAudio = useCallback(
    (audioFile: File) => {
      if (isSending) return;

      onSend({
        to,
        message: messageText.trim() || undefined,
        files: [audioFile],
      });

      setMessageText("");
      setShowRecorder(false);
      focusMessageInput();
    },
    [isSending, onSend, to, messageText, focusMessageInput],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      addFiles(e.dataTransfer.files);
    },
    [addFiles],
  );

  const canSend =
    (messageText.trim().length > 0 || attachedFiles.length > 0) && !isSending;

  return (
    <Paper
      elevation={0}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      sx={{
        position: "relative",
        border: "1px solid",
        borderColor: isDragging ? theme.shell.accent : theme.shell.border,
        borderRadius: 3,
        m: 1,
        overflow: "visible",
        transition: "border-color 0.2s",
        bgcolor: isDragging ? alpha(theme.shell.accent, 0.05) : theme.chat.inputBg,
        color: theme.shell.textPrimary,
      }}
    >
      {isDragging && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <Typography variant="body2" fontWeight={600} color={theme.shell.accent}>
            Suelta aquí para adjuntar
          </Typography>
        </Box>
      )}

      <MediaPreviewStrip files={attachedFiles} onRemove={removeFile} />

      {showRecorder && (
        <AudioRecorder
          onRecordingComplete={(file) => {
            addFiles([file]);
            setShowRecorder(false);
            focusMessageInput();
          }}
          onAutoSend={handleAutoSendAudio}
          messageText={messageText}
          onCancel={() => {
            setShowRecorder(false);
            focusMessageInput();
          }}
        />
      )}

      {!showRecorder && (
        <Box px={1.5} pt={1}>
          <TextField
            inputRef={textFieldRef}
            size="small"
            placeholder="Write a message..."
            fullWidth
            multiline
            maxRows={4}
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyDown={handleKeyDown}
            variant="standard"
            InputProps={{
              disableUnderline: true,
              readOnly: isSending,
            }}
            sx={{
              "& .MuiInputBase-root": {
                fontSize: { xs: 15, sm: 14 },
              },
            }}
          />
        </Box>
      )}

      <Box display="flex" alignItems="center" gap={0.5} px={1} pb={1} pt={0.5}>
        <span>
          <IconButton
            size="small"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => imageInputRef.current?.click()}
            disabled={isSending || attachedFiles.length >= MAX_FILES}
            sx={{ color: theme.shell.textSecondary }}
          >
            <ImageIcon fontSize="small" />
          </IconButton>
        </span>

        <span>
          <IconButton
            size="small"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => videoInputRef.current?.click()}
            disabled={isSending || attachedFiles.length >= MAX_FILES}
            sx={{ color: theme.shell.textSecondary }}
          >
            <VideocamIcon fontSize="small" />
          </IconButton>
        </span>

        <span>
          <IconButton
            size="small"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setShowRecorder((v) => !v)}
            disabled={isSending || attachedFiles.length >= MAX_FILES}
            sx={{
              color: showRecorder ? "error.main" : theme.shell.textSecondary,
              bgcolor: showRecorder ? "error.50" : "transparent",
            }}
          >
            <MicIcon fontSize="small" />
          </IconButton>
        </span>

        <Box flex={1} />

        <Button
          variant="contained"
          size="small"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleSend}
          disabled={!canSend}
          endIcon={<SendIcon fontSize="small" />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            px: 2,
            py: 0.75,
            borderRadius: 2,
            bgcolor: canSend ? theme.shell.accent : alpha(theme.shell.accent, 0.35),
            color: canSend ? "#fff" : alpha("#fff", 0.7),
            boxShadow: "none",
            "&:hover": {
              bgcolor: canSend ? theme.shell.accent : alpha(theme.shell.accent, 0.35),
              opacity: 0.88,
              boxShadow: "none",
            },
          }}
        >
          {isSending ? "..." : "Send"}
        </Button>
      </Box>

      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files) addFiles(e.target.files);
          e.target.value = "";
        }}
      />

      <input
        ref={videoInputRef}
        type="file"
        accept="video/mp4,video/webm,video/ogg,video/quicktime,video/3gpp"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files) addFiles(e.target.files);
          e.target.value = "";
        }}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files) addFiles(e.target.files);
          e.target.value = "";
        }}
      />
    </Paper>
  );
}